/**
 * Sites API service for Whakahaere Safety Management System
 *
 * Migrated to typed `@tiaki/http-client` per ADR 113 §Decision.
 * FormData uploads (`uploadFloorplanSource`, `uploadSiteFloorplan`) fall
 * through to `client.ky` to keep multipart Content-Type handling correct.
 */

import { client } from '../lib/http-client';

function unwrap<T>(result: { data?: T; error?: unknown }): T {
  if (result.error !== undefined) {
    throw result.error;
  }
  return result.data as T;
}

export type SiteStatus = 'active' | 'pending' | 'scoped' | 'inactive';
export type FloorplanStatus = 'pending' | 'processing' | 'completed' | 'failed';

export interface SiteFloorplan {
  source_url: string;
  source_name: string;
  file_name: string;
  bbox?: {
    ne: { lat: number; lng: number };
    sw: { lat: number; lng: number };
  } | null;
  floor_id?: number;
  status?: FloorplanStatus;
  error_message?: string | null;
}

/** @deprecated Use SiteFloorplan */
export type FloorplanSource = SiteFloorplan;

export interface Site {
  id: number;
  public_id: string;
  name: string;
  district: number | null;
  district_name?: string;
  status: SiteStatus;
  address?: string;
  address_2?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  latitude?: string;
  longitude?: string;
  phone_number?: string;
  organization_id?: number;
  organization_name?: string;
  organization_status?: string;
  organization_public_id?: string;
  elevation?: string;
  data?: Record<string, unknown> | null;
  alerts_enabled?: boolean;
  chat_enabled?: boolean;
  maps_enabled?: boolean;
  drill_enabled?: boolean;
  show_safe_unsafe?: boolean;
  licensed?: boolean;
  floorplan?: SiteFloorplan | null;
  /**
   * IPAWS geo targeting (ADR-139): county SAME geocodes for the
   * CAP `<area>` block — "0" + 5-digit county FIPS, e.g. ["049035"]. Derived
   * and stored by `manage.py backfill_ipaws_geo`; null until derived.
   */
  same_geocodes?: string[] | null;
  /**
   * CAP `<polygon>` WGS84 closed ring ("lat,lon lat,lon …") — campus boundary
   * when available, else a point buffer around the site coordinates. Derived
   * and stored by `manage.py backfill_ipaws_geo`; null until derived.
   */
  cap_polygon?: string | null;
}

export interface SiteListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Site[];
}

export interface SiteFilters {
  search?: string;
  status?: SiteStatus;
  page?: number;
  page_size?: number;
  query?: string;
  limit?: number;
  offset?: number;
  ordering?: string;
}
export interface DistrictBbox {
  min_lat?: number | null;
  min_lng?: number | null;
  max_lat?: number | null;
  max_lng?: number | null;
}

export interface District {
  id: number;
  is_sandbox: string;
  use_missing_person_chat: string;
  auth_expiry_days: string;
  auth_refresh_days: string;
  system_admin_email: string;
  industry: string;
  start_alert_button_label: string;
  start_drill_button_label: string;
  start_report_button_label: string;
  is_silent_drill_default: string;
  // Seconds; 0 disables. See District.direct_chat_unread_notification_delay
  // in server/apps/schools/models.py.
  direct_chat_unread_notification_delay: number | string | null;
  jwt_secret: string | null;
  jwt_algorithm: string;
  public_id: string;
  name: string;
  domain: string;
  type: string;
  coverage_type?: 'point_radius' | 'admin_area' | null;
  latitude?: number | null;
  longitude?: number | null;
  service_radius_km?: number | null;
  area_type?: 'city' | 'county' | 'state' | null;
  area_name?: string | null;
  center_lat?: number | null;
  center_lng?: number | null;
  bbox?: DistrictBbox | null;
  boundary_geojson?: unknown;
  state?: string | null;
  data: {
    aim: {
      chat: unknown | null;
      maps: unknown | null;
      reports: { missing: { notify: boolean } };
      settings: {
        email: { systemAdmin: string[] };
        users: {
          auth: { expiryDays: number; refreshDays: number };
          shared: { expiryHours: string };
        };
      };
    };
  };
  ignore_auto_provision_site: boolean;
  is_provider?: boolean;
  can_be_invited_as_provider?: boolean;
  status: string;
  roles: number[];
}
export interface DistrictListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: District[];
}

/**
 * Get list of sites with optional filters
 */
export async function getSites(
  filters: SiteFilters = {},
): Promise<SiteListResponse> {
  const result = await client.GET('/api/v2/site/', {
    params: { query: filters as never },
  });
  return unwrap(result) as unknown as SiteListResponse;
}

/**
 * Get a single site by ID
 */
export async function getSiteById(id: number): Promise<Site> {
  const result = await client.GET('/api/v2/site/{id}/', {
    params: { path: { id } as never },
  });
  return unwrap(result) as unknown as Site;
}

/**
 * Create a new site
 */
export async function createSite(
  data: Omit<Site, 'id' | 'public_id'>,
): Promise<Site> {
  const result = await client.POST('/api/v2/site/', {
    body: data as never,
  });
  return unwrap(result) as unknown as Site;
}

/**
 * Update an existing site
 */
export async function updateSite(
  id: number,
  data: Partial<Omit<Site, 'id' | 'public_id'>>,
): Promise<Site> {
  const result = await client.PATCH('/api/v2/site/{id}/', {
    params: { path: { id } as never },
    body: data as never,
  });
  return unwrap(result) as unknown as Site;
}

/**
 * Delete a site
 */
export async function deleteSite(id: number): Promise<void> {
  const result = await client.DELETE('/api/v2/site/{id}/', {
    params: { path: { id } as never },
  });
  unwrap(result);
}

/**
 * Toggle site status
 */
export async function toggleSiteStatus(
  id: number,
  status: SiteStatus,
): Promise<Site> {
  return updateSite(id, { status });
}
export async function getOrganization(): Promise<DistrictListResponse> {
  // /api/v2/org/ paginates with DRF LimitOffset (default limit
  // 100), so a bare GET silently truncates to the first 100 orgs by name.
  // Dropdown consumers (SiteDrawer, SitesForm, RoleForm, …) need the full
  // list the caller's tier allows — request up to the server's max_limit.
  const result = await client.GET('/api/v2/org/', {
    params: { query: { limit: 1000 } as never },
  });
  return unwrap(result) as unknown as DistrictListResponse;
}

/**
 * Organization (District) admin CRUD.
 *
 * Wraps the existing `/api/v2/org/` DistrictViewSet, which already enforces
 * row-level access in `get_queryset` (Aegix staff see all, vendors see their
 * VendorOrganization-linked districts, regular users see only districts they
 * have schools in). This client wrapper only adds list filters + mutations.
 */
export interface OrganizationFilters {
  query?: string;
  publicId?: string;
  /** Translated to limit/offset by getOrganizations — the backend
   * paginates with DRF LimitOffset and ignores page/page_size. */
  page?: number;
  page_size?: number;
  limit?: number;
  offset?: number;
  ordering?: string;
}

/** Subset of District fields a vendor/Aegix admin can create or edit. */
export type OrganizationWriteInput = Partial<
  Omit<District, 'id' | 'public_id' | 'schools' | 'roles' | 'data'>
>;

export async function getOrganizations(
  filters: OrganizationFilters = {},
): Promise<DistrictListResponse> {
  // The backend paginates with limit/offset and silently ignores
  // page/page_size (every page would pin to the same first 100 rows) —
  // translate so the Organizations table pages server-side.
  const { page, page_size, ...rest } = filters;
  const query: Record<string, unknown> = { ...rest };
  if (page_size !== undefined) {
    query.limit = page_size;
    query.offset = ((page ?? 1) - 1) * page_size;
  }
  const result = await client.GET('/api/v2/org/', {
    params: { query: query as never },
  });
  return unwrap(result) as unknown as DistrictListResponse;
}

export async function getOrganizationById(id: number): Promise<District> {
  const result = await client.GET('/api/v2/org/{id}/', {
    params: { path: { id } as never },
  });
  return unwrap(result) as unknown as District;
}

export async function createOrganization(
  data: OrganizationWriteInput,
): Promise<District> {
  const result = await client.POST('/api/v2/org/', {
    body: data as never,
  });
  return unwrap(result) as unknown as District;
}

export async function updateOrganization(
  id: number,
  data: OrganizationWriteInput,
): Promise<District> {
  const result = await client.PATCH('/api/v2/org/{id}/', {
    params: { path: { id } as never },
    body: data as never,
  });
  return unwrap(result) as unknown as District;
}

/**
 * Delete an organization (District) via DELETE /api/v2/org/{id}/.
 *
 * The backend soft-deletes the org and cascade-archives its schools
 * (ADR-124: District/School are SafeDeleteModel, recoverable by an Aegix
 * admin). The licensing guard returns 403 for licensed sites unless the caller
 * is Aegix/vendor staff — surface that error to the user. The UI gates behind
 * a confirmation dialog.
 */
export async function deleteOrganization(id: number): Promise<void> {
  const result = await client.DELETE('/api/v2/org/{id}/', {
    params: { path: { id } as never },
  });
  unwrap(result);
}

/**
 * Upload a floorplan source image for a floor.
 */
export async function uploadFloorplanSource(
  floorId: number,
  file: File,
  options?: {
    geo_bounds?: FloorplanSource['bbox'];
    auto_bounds?: boolean;
  },
): Promise<FloorplanSource> {
  const formData = new FormData();
  formData.append('file', file);
  if (options?.geo_bounds) {
    formData.append('geo_bounds', JSON.stringify(options.geo_bounds));
  }
  if (options?.auto_bounds) {
    formData.append('auto_bounds', 'true');
  }
  // Multipart upload — bypass openapi-fetch typed body in favor of ky direct.
  const response = await client.ky.post(
    `api/v2/floor/${floorId}/floorplan-source/`,
    { body: formData },
  );
  return (await response.json()) as FloorplanSource;
}

/**
 * Get floorplan source for a floor.
 */
export async function getFloorplanSource(
  floorId: number,
): Promise<FloorplanSource | null> {
  const result = await client.GET('/api/v2/floor/{id}/floorplan-source/', {
    params: { path: { id: floorId } as never },
  });
  return unwrap(result) as unknown as FloorplanSource | null;
}

/**
 * Update geo bounds for a floor's floorplan source.
 */
export async function updateFloorplanSourceBounds(
  floorId: number,
  geo_bounds: FloorplanSource['bbox'],
): Promise<FloorplanSource> {
  const result = await client.PATCH('/api/v2/floor/{id}/floorplan-source/', {
    params: { path: { id: floorId } as never },
    body: { geo_bounds } as never,
  });
  return unwrap(result) as unknown as FloorplanSource;
}

/**
 * Delete a floor's floorplan source.
 */
export async function deleteFloorplanSource(floorId: number): Promise<void> {
  const result = await client.DELETE('/api/v2/floor/{id}/floorplan-source/', {
    params: { path: { id: floorId } as never },
  });
  unwrap(result);
}

/**
 * Upload a floorplan for a site. Backend auto-creates Building/Floor if needed.
 */
export async function uploadSiteFloorplan(
  siteId: number,
  file: File,
  licensed: boolean,
): Promise<SiteFloorplan> {
  const formData = new FormData();
  formData.append('file', file);
  if (licensed) {
    formData.append('licensed', 'true');
  }
  const response = await client.ky.post(`api/v2/site/${siteId}/floorplan/`, {
    body: formData,
  });
  return (await response.json()) as SiteFloorplan;
}

/**
 * Get floorplan for a site.
 */
export async function getSiteFloorplan(
  siteId: number,
): Promise<SiteFloorplan | null> {
  const result = await client.GET('/api/v2/site/{id}/floorplan/', {
    params: { path: { id: siteId } as never },
  });
  return unwrap(result) as unknown as SiteFloorplan | null;
}

/**
 * Delete a site's floorplan.
 */
export async function deleteSiteFloorplan(siteId: number): Promise<void> {
  const result = await client.DELETE('/api/v2/site/{id}/floorplan/', {
    params: { path: { id: siteId } as never },
  });
  unwrap(result);
}

// ---------------------------------------------------------------------------
// Site Blueprints — ad-hoc multi-artifact attachments per site.
//
// Many-per-site, free-form label, lifecycle: Pending → Submitted → Complete
// (Failed sideband). Backed by the `/api/v2/site-blueprint/` ViewSet; CRUD is
// standard, and replacing the file uses the dedicated `file/` sub-action so
// the status-reset semantics live server-side.
// ---------------------------------------------------------------------------

export type BlueprintStatus = 'pending' | 'submitted' | 'complete' | 'failed';

export interface SiteBlueprint {
  id: number;
  school: number;
  label: string;
  file_url: string | null;
  file_name: string;
  status: BlueprintStatus;
  error_message: string;
  created_at: string;
  updated_at: string;
}

export interface SiteBlueprintListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: SiteBlueprint[];
}

const blueprintsBase = 'api/v2/site-blueprint/';

export async function listSiteBlueprints(
  siteId: number,
): Promise<SiteBlueprint[]> {
  const response = await client.ky.get(blueprintsBase, {
    searchParams: { school: String(siteId), page_size: '100' },
  });
  const payload = (await response.json()) as
    SiteBlueprint[] | SiteBlueprintListResponse;
  return Array.isArray(payload) ? payload : (payload.results ?? []);
}

export async function createSiteBlueprint(
  siteId: number,
  file: File,
  label: string,
): Promise<SiteBlueprint> {
  const formData = new FormData();
  formData.append('school', String(siteId));
  formData.append('label', label);
  formData.append('file', file);
  const response = await client.ky.post(blueprintsBase, { body: formData });
  return (await response.json()) as SiteBlueprint;
}

export async function updateSiteBlueprint(
  blueprintId: number,
  patch: Partial<Pick<SiteBlueprint, 'label'>>,
): Promise<SiteBlueprint> {
  const response = await client.ky.patch(`${blueprintsBase}${blueprintId}/`, {
    json: patch,
  });
  return (await response.json()) as SiteBlueprint;
}

export async function replaceSiteBlueprintFile(
  blueprintId: number,
  file: File,
): Promise<SiteBlueprint> {
  const formData = new FormData();
  formData.append('file', file);
  const response = await client.ky.post(
    `${blueprintsBase}${blueprintId}/file/`,
    { body: formData },
  );
  return (await response.json()) as SiteBlueprint;
}

export async function deleteSiteBlueprint(blueprintId: number): Promise<void> {
  await client.ky.delete(`${blueprintsBase}${blueprintId}/`);
}
